/**
 * Shared formatting and vocabulary for the YouGuide One pages.
 *
 * In one file so the three pages cannot disagree about what "paid" looks like
 * or how a price is rounded — an order that reads $0.07 on one screen and
 * $0.1 on another is a support call.
 */

/**
 * Prices here are small: a hundred megabytes of Spain is seven cents. Always
 * two decimals, never a bare "0", or a real sale looks like a free one.
 *
 * THE SYMBOL IS PASSED IN. Every figure on these pages is in the STORE
 * currency, and this printed '$' for all of them — a EUR 2.57 sale read
 * $2.57. The server sends `currencySymbol` on orders and on settings; callers
 * that have it pass it, and the fallback below is only for a figure rendered
 * before a response has arrived.
 */
export const money = (amount, symbol = DEFAULT_SYMBOL) => {
    const n = Number(amount);
    return Number.isFinite(n) ? `${symbol}${n.toFixed(2)}` : '—';
};

/** Per-GB is the unit every commercial conversation about this uses. */
export const perGb = (perMb, symbol = DEFAULT_SYMBOL) => {
    const n = Number(perMb);
    return Number.isFinite(n) ? `${symbol}${(n * 1024).toFixed(2)}` : '—';
};

/**
 * Shown only until a response carrying the real symbol arrives. Deliberately
 * the euro sign rather than a dollar: the store prices in EUR by
 * configuration, so a placeholder that says dollars would be wrong in the one
 * direction that matters.
 */
const DEFAULT_SYMBOL = '\u20ac';

export const data = (mb) => {
    const n = Number(mb);
    if (!Number.isFinite(n)) return '—';
    if (n < 1024) return `${n} MB`;
    const gb = n / 1024;
    return `${gb.toFixed(gb % 1 === 0 ? 0 : 1)} GB`;
};

export const dateTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

/**
 * Order status, phrased for an operator rather than echoing the enum.
 *
 * `paid` is split from fulfilment on purpose: an order can be paid and its
 * eSIM not yet delivered for hours, and that is a normal state. Showing one
 * word for both would make a healthy queue look like a backlog of failures.
 */
export const ORDER_STATUS = {
    pending_payment: { label: 'Awaiting payment', colour: 'orange' },
    paid: { label: 'Paid', colour: 'green' },
    partial: { label: 'Part delivered', colour: 'gold' },
    completed: { label: 'Delivered', colour: 'green' },
    failed: { label: 'Payment failed', colour: 'red' },
    cancelled: { label: 'Cancelled', colour: 'default' },
    refunded: { label: 'Refunded', colour: 'purple' },
};

export const statusOf = (s) =>
    ORDER_STATUS[s] || { label: s || 'Unknown', colour: 'default' };

/**
 * Refund refusals, phrased as what to do instead.
 *
 * Each of these is a different action for the operator, which is why the
 * backend returns distinct codes rather than one "failed".
 */
export const REFUND_ERRORS = {
    guest_order:
        'This order has no account, so there is no wallet to credit. Refund it at the payment provider instead.',
    already_refunded:
        'This order has already been refunded in full.',
    not_refundable:
        'Only a paid order can be refunded.',
    bad_amount:
        'That amount is not valid for this order.',
};

export const refundErrorText = (err) =>
    REFUND_ERRORS[err?.code] || err?.message || 'Could not refund that order.';

/** Margin, and whether it is one. A negative margin must be impossible to miss. */
export const marginTone = (margin) => {
    const n = Number(margin);
    if (!Number.isFinite(n)) return 'default';
    if (n < 0) return 'red';
    if (n === 0) return 'orange';
    return 'green';
};
